import { HttpEventType } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { map, Observable, of, startWith, Subscription } from 'rxjs';
import { CheckPageAccess } from 'src/app/components/authorize/check-page-access';
import { MaterialList, MaterialListDefaultValues, MaterialQueryFilters, MaterialQueryFiltersDefaultValues } from 'src/app/components/materials/material-list';
import { UpdateMaterial, UpdateMaterialDefaultValues } from 'src/app/components/materials/materials';
import { FileService } from 'src/app/services/file.service';
import { MaterialsService } from 'src/app/services/materials.service';
import { ModalService } from 'src/app/services/modal.service';
import { TagsService } from 'src/app/services/tag-service.service';
import { ToastService } from 'src/app/services/toast-service.service';

@Component({
    selector: 'app-edit-material',
    templateUrl: './edit-material.component.html',
    styleUrls: ['./edit-material.component.scss'],
    standalone: false
})
export class EditMaterialComponent {
  selectedFile: File | null = null;
  tags: any[] = [];
  material: UpdateMaterial = UpdateMaterialDefaultValues();
  hideReviewPanel: boolean = true;
  materialTypes: any[] = [];
  selectedTags: any[] = [];
  materialStatus: any[] = [];
  materialLanguage: any[] = [];
  selectedFileName: string = 'Drag and drop your file here or click to upload';
  isSubmitted: boolean = false;
  id: string = '';
  modalSub!: Subscription;

  modalOpen = this._modalservice.getModalState();
  modalContent = '';
  modalTitle = '';
  modalOkButtonValue = 'OK';
  modalCancelButtonValue = 'CANCEL';
  modalOkButtonText = 'Ok';
  modalCancelButtonText = 'Cancel';
  showReplacementDiv: boolean = false;
  replacementMaterial: MaterialList[] = [];
  queryfilter: MaterialQueryFilters = MaterialQueryFiltersDefaultValues();
  filteredOptions!: Observable<MaterialList[]>;
  myControl = new FormControl<string | MaterialList>('');
  selectedMaterial: MaterialList = MaterialListDefaultValues();
  showReplacementFileSelector: boolean = false;
  replacementRadioValue: string = "2";
  pageName = CheckPageAccess.EDITMATERIAL
  showFileDescDiv: boolean = true;
  isUploading: boolean = false;
  isSaving: boolean = false;
  uploadProgress: number = 0;
  isUploadComplete: boolean = false;

  constructor(private tagservice: TagsService,
    private materialservice: MaterialsService,
    private fileservice: FileService,
    private toastservice: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private _modalservice: ModalService) {
  }

  async ngOnInit(): Promise<void> {
    await this.route.params.subscribe({
      next:params => {
        this.id = params['id'];
      }
    });

    this.materialTypes = this.materialservice.getMaterialTypes();
    this.materialservice.getMaterialLanguages().subscribe({
      next:languages => {
        this.materialLanguage = languages;
      }
    });

    await this.tagservice.getActiveTags().subscribe({
      next:(resp) => {
        this.tags = resp;
      }
    });

    await this.materialservice.getMaterialStatus("EDIT", this.id).subscribe({
      next:(resp) => {
        this.materialStatus = resp;
      }
    });

    await this.materialservice.getMaterialDetailsforUpdate(this.id).subscribe({
      next:(resp) => {
        this.material = resp;
        this.selectedTags = resp.tags;
      }
    });

    await this.materialservice.getReplacementMaterials().subscribe({
      next:(resp) => {
        this.replacementMaterial = resp;
      },
      error:(error) => {},
      complete:() => {
        this.filteredOptions = this.myControl.valueChanges.pipe(
          startWith(''),
          map(value => {
            const name = typeof value === 'string' ? value : value?.materialName;
            return name ? this._filter(name as string) : this.replacementMaterial.slice();
          }),
        );
      }
    });

    this.afterConfirmModalOps();
  }

  private _filter(name: string): MaterialList[] {
    const filterValue = name.toLowerCase();
    return this.replacementMaterial.filter(option => option.materialName.toLowerCase().includes(filterValue));
  }

  displayFn(material: MaterialList): string {
    return material && material.materialName ? material.materialName : '';
  }

  updateMaterialFiltering() {
    this.filteredOptions = of(this.replacementMaterial)
      .pipe(
        map(material => this.filterMaterial(material)),
      );
  }

  filterMaterial(values: any[]) {

    const searchingString = typeof this.selectedMaterial === "string";
    if (searchingString) {
      const searchValue = this.selectedMaterial?.toString().toLowerCase();
      return values.filter(material => material.materialName.toLowerCase().startsWith(searchValue));
    }

    return values.filter(material => material.materialName.toLowerCase().startsWith(this.selectedMaterial));
  }


  onSelect(event: NgxDropzoneChangeEvent) {
    this.selectedFileName = '';

    const file = event.addedFiles[0]; // Get the first file selected

    if (file) {
      this.selectedFileName = file.name;
      const formData = new FormData();
      formData.append('file', file);

      this.isUploading = true;
      this.isUploadComplete = false;
      this.uploadProgress = 0;
      this.showFileDescDiv = false;
      this.fileservice.uploadMaterialFile(formData, this.material.materialType).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            // Keep under 100% until the server finishes and returns a response
            this.uploadProgress = Math.min(99, Math.round((100 * event.loaded) / event.total));
          } else if (event.type === HttpEventType.Response) {
            this.material.materialFileId = event.body.id;
            this.material.materialFileName = event.body.fileName;
            this.uploadProgress = 100;
            this.isUploadComplete = true;
            setTimeout(() => {
              this.showFileDescDiv = true;
              this.isUploading = false;
              this.isUploadComplete = false;
            }, 1500);
          }
        },
        error: () => {
          this.isUploading = false;
          this.isUploadComplete = false;
          this.uploadProgress = 0;
          this.selectedFileName = 'Drag and drop your file here or click to upload';
          this.toastservice.showErrorMessage('File upload failed. Please try again.');
        }
      });
    }
  }

  onFileSelected(event: any) {
    this.selectedFile = event.target.files[0];
  }

  onSubmit(form: NgForm) {
    this.isSubmitted = true;
    this.material.tags = this.selectedTags;

    if(!this.validateReqFileds(form)) {
      this.toastservice.showErrorMessage('Please fill all the required fields');
      return;
    }

    if (this.isUploading || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.material.replacementMaterialId = this.selectedMaterial.id;
    this.materialservice.updateMaterial(this.material).subscribe({
      next: (resp) => {
        this.isSaving = false;
        this.toastservice.showMessage("Material Updated Successfully");
        this.router.navigate(['/material-list']);
      },
      error: () => {
        this.isSaving = false;
        this.toastservice.showErrorMessage('Failed to update material. Please try again.');
      }
    });
  }

  getTagName(id: string): string {
    const tag = this.tags.find(t => t.id === id);
    return tag ? tag.tagName : '';
  }

  validateReqFileds(form: NgForm): boolean{

    var validated = true;
    if (this.material.materialName == null || this.material.materialName == '') {
      validated = false;
    }

    if (this.material.materialType == null || this.material.materialType == 0) {
      validated = false;
    }

    if (this.material.materialFileId == null || this.material.materialFileId == 0) {
      validated = false;
    }

    if( this.material.language == null || this.material.language == 0) {
      validated = false;
    }

    if (this.material.status == 0) {
      validated = false;
    }

    return validated;
  }

  askConfirmation(
    okButtonText: string,
    okButtonValue: string,
    cancelButtonText: string,
    cancelButtonValue: string,
    content: string,
    title: string) {
      this.modalContent = content;
      this.modalTitle = title;
      this.modalOkButtonValue = okButtonValue;
      this.modalCancelButtonValue = cancelButtonValue;
      this.modalOkButtonText = okButtonText;
      this.modalCancelButtonText = cancelButtonText;

      this._modalservice.openModal();
  }

  afterConfirmModalOps() {
    this.modalSub = this._modalservice
      .getButtonClicked()
      .subscribe((button) => {
        switch (button) {
          case 'YES':
            this.router.navigate(['/material-list']);
            break;
            case 'NO':
              this._modalservice.closeModal();
              break;
          default:
            this._modalservice.closeModal();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.modalSub) {
      this.modalSub.unsubscribe();
    }
  }

  cancel() {
    this.askConfirmation('Yes, Cancel', 'YES', 'No, Go Back', 'NO', 'Are you sure you want to cancel? Any unsaved changes will be lost', 'Unsaved Data');
  }

  downloadFile() {
    this.fileservice.downloadMaterialFile(this.material.materialFileId).subscribe({
      next: (response) => {
        window.open(response.url, '_blank');
      }
    })
  }

  statusChange() {
    if (this.material.status == 3) {
      this.showReplacementDiv = true;
    } else {
      this.showReplacementDiv = false;
    }
  }

  replacementRadioChange() {
    if (this.replacementRadioValue == "1") {
      this.showReplacementFileSelector = true;
      this.material.isReplaced = true;
    } else {
      this.showReplacementFileSelector = false;
      this.material.isReplaced = false;
    }
  }

  removeMaterialFile() {
    this.material.materialFileName = '';
    this.material.materialFileId = 0;
    this.showFileDescDiv = false;
    this.isUploadComplete = false;
    this.uploadProgress = 0;
  }
}

